import Axios, { AxiosInstance } from "axios";
import config from "../config.ts";

export const DASHBOARD_TOKEN_KEY = "dashboardToken";

export interface AdminToken {
  id: string;
  name: string;
  token: string;
  createdAt: string;
}

export interface SmtpFingerprint {
  hostname: string;
  banner: string;
  greetingOverride: string;
  ehloSuffix: string;
  ehloFeatures: string[];
  okResponse: string;
  mailAcceptedResponse: string;
  rcptAcceptedResponse: string;
  dataQueuedResponse: string;
  byeResponse: string;
  startTlsResponse: string;
  vrfyCode: number;
  vrfyMessage: string;
  helpCode: number;
  helpMessage: string;
  tooSoonResponse: string;
  unknownCommandResponse: string;
  hideXClient: boolean;
}

export interface Settings {
  domains: string[];
  senderAllows: string[];
  rateLimitMaxAttempts: number;
  rateLimitWindowMinutes: number;
  smtpFingerprint: SmtpFingerprint;
}

export interface Announcement {
  id: string;
  content: string;
  enabled: boolean;
  origins: string[];
}

export interface SystemStats {
  date: string;
  uptimeSeconds: number;
  domains: string[];
  senderAllows: string[];
  usersByDomain: Record<string, number>;
  totalUsers: number;
  todayMailCount: number;
  mailTtlSeconds: number;
  memory: { totalBytes: number; usedBytes: number; freeBytes: number; usedPercent: number };
  cpu: { loadAvg: number[]; cores: number };
  platform: string;
  arch: string;
  httpPort: number;
  debug: boolean;
  rateLimit: { max: number; windowMinutes: number };
}

export interface UserListItem {
  username: string;
  domain: string;
}

export interface AdminMail {
  id: string;
  messageId: string;
  html: string;
  text: string;
  subject: string;
  sender: { address: string; name: string };
  receiver: { address: string; name: string };
  date: string;
  headers: Record<string, string>;
}

export default class AdminAPI {
  private client: AxiosInstance;

  constructor() {
    this.client = Axios.create({ baseURL: config.apiUrl });
    this.client.interceptors.request.use((cfg) => {
      const token = localStorage.getItem(DASHBOARD_TOKEN_KEY);
      if (token) cfg.headers.Authorization = `Bearer ${token}`;
      return cfg;
    });
  }

  private async request<T = any>(method: string, url: string, data?: any, isForm = false): Promise<T> {
    const response = await this.client.request({
      method,
      url,
      data,
      validateStatus: () => true,
      headers: isForm ? undefined : { 'Content-Type': 'application/json' }
    });
    if (response.status === 401 || response.status === 403) {
      this.clearToken();
      throw new Error(response.data?.error || 'Unauthorized');
    }
    if (!response.data?.success) {
      throw new Error(response.data?.error || `Request failed (${response.status})`);
    }
    return response.data as T;
  }

  saveToken(token: string) {
    localStorage.setItem(DASHBOARD_TOKEN_KEY, token);
  }

  clearToken() {
    localStorage.removeItem(DASHBOARD_TOKEN_KEY);
  }

  getToken(): string | null {
    return localStorage.getItem(DASHBOARD_TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  async setupStatus(): Promise<{ required: boolean }> {
    return this.request('GET', '/admin/setup-status');
  }

  async setup(setupToken: string, username: string, password: string): Promise<{ token: string; username: string }> {
    const r = await this.request<any>('POST', '/admin/setup', { setupToken, username, password });
    this.saveToken(r.token);
    return r;
  }

  async login(username: string, password: string): Promise<{ token: string; username: string }> {
    const r = await this.request<any>('POST', '/admin/login', { username, password });
    this.saveToken(r.token);
    return r;
  }

  async me(): Promise<{ username: string }> {
    return this.request('GET', '/admin/me');
  }

  async changeMyPassword(oldPassword: string, newPassword: string): Promise<void> {
    await this.request('POST', '/admin/me/change-password', { oldPassword, newPassword });
  }

  async getStats(): Promise<SystemStats> {
    const r = await this.request<any>('GET', '/admin/stats');
    return r.stats;
  }

  async listUsers(opts: { search?: string; domain?: string; page?: number; pageSize?: number } = {}): Promise<{ items: UserListItem[]; total: number; page: number; pageSize: number }> {
    const params = new URLSearchParams();
    if (opts.search) params.set('search', opts.search);
    if (opts.domain) params.set('domain', opts.domain);
    params.set('page', String(opts.page || 1));
    params.set('pageSize', String(opts.pageSize || 50));
    const r = await this.request<any>('GET', `/admin/users?${params.toString()}`);
    return r;
  }

  async getUserInbox(username: string): Promise<AdminMail[]> {
    const r = await this.request<any>('GET', `/admin/users/${encodeURIComponent(username)}/inbox`);
    return r.mails || [];
  }

  async getSettings(): Promise<Settings> {
    const r = await this.request<any>('GET', '/admin/settings');
    return r.settings;
  }

  async updateSettings(s: Partial<Settings>): Promise<Settings> {
    const r = await this.request<any>('PUT', '/admin/settings', s);
    return r.settings;
  }

  async getAnnouncement(): Promise<Announcement | null> {
    const r = await this.request<any>('GET', '/admin/announcement');
    return r.announcement;
  }

  async updateAnnouncement(content: string, enabled: boolean, origins: string[]): Promise<Announcement | null> {
    const r = await this.request<any>('PUT', '/admin/announcement', { content, enabled, origins });
    return r.announcement;
  }

  async listAdminTokens(): Promise<AdminToken[]> {
    const r = await this.request<any>('GET', '/admin/admin-tokens');
    return r.tokens;
  }

  async createAdminToken(name: string): Promise<AdminToken> {
    const r = await this.request<any>('POST', '/admin/admin-tokens', { name });
    return r.token;
  }

  async deleteAdminToken(id: string): Promise<void> {
    await this.request('DELETE', `/admin/admin-tokens/${id}`);
  }

  async bulkCreateUsers(input: { file?: File; data?: string }): Promise<string> {
    return this.bulkSend('/admin/users/bulk-create', input);
  }

  async bulkChangePassword(input: { file?: File; data?: string }): Promise<string> {
    return this.bulkSend('/admin/users/bulk-change-password', input);
  }

  private async bulkSend(url: string, input: { file?: File; data?: string }): Promise<string> {
    const form = new FormData();
    if (input.file) form.append('file', input.file);
    else if (input.data) form.append('data', input.data);
    else throw new Error('File or data is required');

    const response = await this.client.request({
      method: 'POST',
      url,
      data: form,
      validateStatus: () => true,
      responseType: 'text'
    });
    if (response.status === 401 || response.status === 403) {
      this.clearToken();
      throw new Error('Unauthorized');
    }
    if (response.status >= 400) {
      throw new Error(typeof response.data === 'string' ? response.data : 'Request failed');
    }
    return response.data as string;
  }
}
